import { ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { useNavigate } from '@tanstack/react-router';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import CalendarControls from '~/components/CalendarControls';
import DrawerButton from '~/containers/DrawerButton';
import WeekplanViewSwitcher, {
  WeekplanView,
} from '~/components/WeekplanViewSwitcher';
import WeekActionsMenu from '~/containers/Weekplan/WeekActionsMenu';
import { PRIVATE } from '~/components/ShiftDetailCardApplicationStatus/constants';
import { COMMITTED } from '~/components/ShiftDetailCardAssignmentStatus/constants';
import messages from '~/containers/Weekplan/messages';
import { useShiftsForCalendarWeek } from '~/hooks/api/useShiftsForCalendarWeek';
import { useCutoffDates } from '~/hooks/api/useCutoffDates';

type Props = {
  activeView: WeekplanView;
  year: number;
  week: number;
  children: ReactNode;
};

export const WeekplanLayout = ({ activeView, year, week, children }: Props) => {
  const navigate = useNavigate();
  const weekStart = useMemo(
    () => moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek'),
    [year, week]
  );

  const cutOffDatesQuery = useCutoffDates();
  const shiftsQuery = useShiftsForCalendarWeek({
    year,
    week,
    options: {
      refetchInterval: 1000,
    },
  });

  const hasShifts = shiftsQuery.data ? shiftsQuery.data.length > 0 : false;
  const hasPrivateShifts = shiftsQuery.data
    ? shiftsQuery.data.some((shift) => shift.applicationState === PRIVATE)
    : false;
  const hasCommittedShifts = shiftsQuery.data
    ? shiftsQuery.data.some((shift) => shift.assignmentState === COMMITTED)
    : false;

  const handleDateChange = (date: Date) => {
    const m = moment(date);
    const newYear = m.isoWeekYear().toString();
    const newWeek = m.isoWeek().toString();
    if (activeView === 'overview') {
      navigate({
        to: '/weekplan/$year/$week/overview',
        params: { year: newYear, week: newWeek },
        search: { highlightPublisher: undefined, sidebarView: undefined },
      });
      return;
    }
    navigate({
      to: '/weekplan/$year/$week/$day/{-$shiftId}',
      params: {
        year: newYear,
        week: newWeek,
        day: m.weekday().toString(),
      },
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <DrawerButton />
          <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
            <FormattedMessage {...messages.header} />
          </Typography>
          {shiftsQuery.data && cutOffDatesQuery.data ? (
            <WeekActionsMenu
              cutoffDates={cutOffDatesQuery.data}
              week={week}
              year={year}
              weekHasShifts={hasShifts}
              weekHasCommittedShifts={hasCommittedShifts}
              weekHasPrivateShifts={hasPrivateShifts}
            />
          ) : null}
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          py: 1,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <CalendarControls date={weekStart} onChange={handleDateChange} />
        <WeekplanViewSwitcher activeView={activeView} year={year} week={week} />
      </Box>
      {children}
    </Box>
  );
};

export default WeekplanLayout;
